import React, { useState } from 'react';
import { Trash2, AlertTriangle, X } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

const DeleteAccountModal = ({ onClose, onDeleted }) => {
  const [step, setStep] = useState(1);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    if (confirmText !== 'DELETE') {
      setError('Please type DELETE to confirm.');
      return;
    }
    setDeleting(true);
    setError('');
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (user) {
        await supabase?.from('user_profiles')?.update({ is_active: false })?.eq('id', user?.id);
        await supabase?.auth?.signOut();
        onDeleted?.();
      }
    } catch (err) {
      setError(err?.message || 'Account deletion failed. Please contact support.');
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 text-red-600" />
            </div>
            <h3 className="font-semibold text-slate-800">Delete Account</h3>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5">
          {step === 1 ? (
            <>
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                <p className="text-sm text-red-700 font-medium mb-2">This action is permanent and cannot be undone.</p>
                <ul className="text-sm text-red-600 space-y-1 list-disc list-inside">
                  <li>All your ride and delivery history will be deleted</li>
                  <li>Saved payment methods will be removed</li>
                  <li>Referral rewards will be forfeited</li>
                  <li>Your account cannot be recovered</li>
                </ul>
              </div>
              <div className="flex gap-3">
                <button onClick={onClose} className="btn-secondary flex-1">
                  Keep Account
                </button>
                <button onClick={() => setStep(2)} className="btn-danger flex-1">
                  Continue
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-600 mb-4">
                To confirm deletion, type <span className="font-bold text-red-600">DELETE</span> in the box below:
              </p>
              <input
                type="text"
                value={confirmText}
                onChange={e => setConfirmText(e?.target?.value)}
                placeholder="Type DELETE to confirm"
                className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-400 mb-3"
                autoFocus
              />
              {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
              <div className="flex gap-3">
                <button onClick={onClose} className="btn-secondary flex-1">
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting || confirmText !== 'DELETE'}
                  className="btn-danger flex-1"
                >
                  {deleting ? 'Deleting...' : 'Delete My Account'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const DangerZone = ({ onDeleted }) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
        <h3 className="font-semibold text-red-700 mb-1 flex items-center gap-2">
          <Trash2 className="w-4 h-4" />
          Danger Zone
        </h3>
        <p className="text-sm text-red-600 mb-4">Once you delete your account, there is no going back. Please be certain.</p>
        <button
          onClick={() => setShowModal(true)}
          className="btn-danger flex items-center gap-2"
        >
          <Trash2 className="w-4 h-4" />
          Delete Account
        </button>
      </div>
      {showModal && (
        <DeleteAccountModal onClose={() => setShowModal(false)} onDeleted={onDeleted} />
      )}
    </>
  );
};

export default DangerZone;
